import { useState, useRef, useEffect } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = ({ content, settings }) => {
  const extra = content?.extra_fields || {};

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    organisation: '',
    objet: '',
    message: '',
    rgpd_consent: false,
    website: '' // honeypot — must stay empty
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const formLoadedAt = useRef(Date.now());

  useEffect(() => {
    // Reset timer when the form is shown again after a successful submit
    if (!submitted) formLoadedAt.current = Date.now();
  }, [submitted]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    if (!formData.objet.trim()) newErrors.objet = "L'objet est requis";
    if (!formData.message.trim()) newErrors.message = 'Le message est requis';
    if (!formData.rgpd_consent) newErrors.rgpd_consent = 'Vous devez accepter la politique de confidentialité';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        form_loaded_at: formLoadedAt.current
      };
      const response = await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      toast.success(response.data.message || 'Message envoyé avec succès');
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        organisation: '',
        objet: '',
        message: '',
        rgpd_consent: false,
        website: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du message");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-inter font-medium tracking-wide uppercase rounded-full mb-4">
            {extra.badge_text || 'Nous écrire'}
          </span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            {content?.title || 'Contact'}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#1e3a8a] to-[#D4AF37] mx-auto mb-6" />
          <p className="text-lg text-slate-600 font-inter max-w-2xl mx-auto">
            {content?.content || "Pour toute question ou demande d'information, n'hésitez pas à nous contacter."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-slate-50 p-8 h-full">
              <h3 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-8">
                {extra.info_title || 'Coordonnées'}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1e3a8a] rounded flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-inter font-medium text-[#0F172A] mb-1">
                      {extra.address_label || 'Adresse'}
                    </h4>
                    <p className="text-slate-600 font-inter text-sm leading-relaxed">
                      {settings?.address || '2 avenue de Marbotte'}<br />
                      21000 Dijon
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1e3a8a] rounded flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-inter font-medium text-[#0F172A] mb-1">
                      {extra.phone_label || 'Téléphone'}
                    </h4>
                    <a 
                      href={`tel:${settings?.phone || '0380659100'}`}
                      className="text-slate-600 font-inter text-sm hover:text-[#1e3a8a] transition-colors"
                    >
                      {settings?.phone || '03 80 65 91 00'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1e3a8a] rounded flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-inter font-medium text-[#0F172A] mb-1">
                      {extra.email_label || 'Email'}
                    </h4>
                    <a 
                      href={`mailto:${settings?.email || 'contact@placefinanciere-bfc.fr'}`}
                      className="text-slate-600 font-inter text-sm hover:text-[#1e3a8a] transition-colors break-all"
                    >
                      {settings?.email || 'contact@placefinanciere-bfc.fr'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Decorative element */}
              <div className="mt-8 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500 font-inter italic">
                  "{extra.quote || "La Place Financière est ouverte à tous les dirigeants d'entreprises."}"
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-green-50 p-12 text-center" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                <h3 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
                  Message envoyé !
                </h3>
                <p className="text-slate-600 font-inter mb-6">
                  {settings?.confirmation_message || 'Nous vous répondrons dans les meilleurs délais.'}
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                  data-testid="contact-new-message-btn"
                >
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Anti-spam honeypot: hidden from real users (visually + for screen readers + for tabbing) */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '-10000px',
                    top: 'auto',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                  }}
                >
                  <Label htmlFor="website">Site web (ne pas remplir)</Label>
                  <Input
                    id="website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    data-testid="contact-honeypot-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="nom" className="font-inter text-sm font-medium text-slate-700 mb-2 block">
                      {extra.form_nom || 'Nom'} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => handleChange('nom', e.target.value)}
                      className={`h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 ${errors.nom ? 'border-red-500' : ''}`}
                      placeholder="Votre nom"
                      data-testid="contact-nom-input"
                    />
                    {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
                  </div>

                  <div>
                    <Label htmlFor="prenom" className="font-inter text-sm font-medium text-slate-700 mb-2 block">
                      {extra.form_prenom || 'Prénom'} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="prenom"
                      value={formData.prenom}
                      onChange={(e) => handleChange('prenom', e.target.value)}
                      className={`h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 ${errors.prenom ? 'border-red-500' : ''}`}
                      placeholder="Votre prénom"
                      data-testid="contact-prenom-input"
                    />
                    {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="font-inter text-sm font-medium text-slate-700 mb-2 block">
                      {extra.form_email || 'Email'} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="votre@email.fr"
                      data-testid="contact-email-input"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <Label htmlFor="telephone" className="font-inter text-sm font-medium text-slate-700 mb-2 block">
                      {extra.form_telephone || 'Téléphone'}
                    </Label>
                    <Input
                      id="telephone"
                      value={formData.telephone}
                      onChange={(e) => handleChange('telephone', e.target.value)}
                      className="h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                      placeholder="06 00 00 00 00"
                      data-testid="contact-telephone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="organisation" className="font-inter text-sm font-medium text-slate-700 mb-2 block">
                    {extra.form_organisation || 'Organisation / Société'}
                  </Label>
                  <Input
                    id="organisation"
                    value={formData.organisation}
                    onChange={(e) => handleChange('organisation', e.target.value)}
                    className="h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                    placeholder="Nom de votre organisation"
                    data-testid="contact-organisation-input"
                  />
                </div>

                <div>
                  <Label htmlFor="objet" className="font-inter text-sm font-medium text-slate-700 mb-2 block">
                    {extra.form_objet || 'Objet'} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="objet"
                    value={formData.objet}
                    onChange={(e) => handleChange('objet', e.target.value)}
                    className={`h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 ${errors.objet ? 'border-red-500' : ''}`}
                    placeholder="Objet de votre message"
                    data-testid="contact-objet-input"
                  />
                  {errors.objet && <p className="text-red-500 text-xs mt-1">{errors.objet}</p>}
                </div>

                <div>
                  <Label htmlFor="message" className="font-inter text-sm font-medium text-slate-700 mb-2 block">
                    {extra.form_message || 'Message'} <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className={`min-h-[150px] rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 ${errors.message ? 'border-red-500' : ''}`}
                    placeholder="Votre message..."
                    data-testid="contact-message-input"
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="rgpd"
                    checked={formData.rgpd_consent}
                    onCheckedChange={(checked) => handleChange('rgpd_consent', checked)}
                    className={`mt-1 ${errors.rgpd_consent ? 'border-red-500' : ''}`}
                    data-testid="contact-rgpd-checkbox"
                  />
                  <div>
                    <Label htmlFor="rgpd" className="font-inter text-sm text-slate-600 cursor-pointer">
                      {extra.form_rgpd || "J'accepte que mes données soient traitées conformément à la politique de confidentialité."}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    {errors.rgpd_consent && <p className="text-red-500 text-xs mt-1">{errors.rgpd_consent}</p>}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-[#1e3a8a] hover:bg-[#172554] text-white px-12 py-6 text-base font-inter font-medium rounded-full transition-all duration-300 hover:shadow-xl disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      {extra.form_submit || 'Envoyer le message'}
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
